import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Mail, CheckCircle2, Star } from 'lucide-react';
import { businessInfo, services, gallery, testimonials } from '../mock';
import ServiceCard from '../components/ServiceCard';
import GalleryGrid from '../components/GalleryGrid';
import TestimonialCard from '../components/TestimonialCard';
import SEO from '../components/SEO';

const Home = () => {
  return (
    <div className="home-page">
      <SEO 
        title="SBF Home Improvements LLC - Quality Home Improvement Services | Mt Pleasant, PA 15666"
        description="Professional roofing, siding, flooring, deck construction & home repairs in Mt Pleasant (15666) & 20-mile radius. Licensed & Insured (PA #159448). Call (724) 420-6192 for free estimates."
        keywords="home improvement 15666, roofing contractor Mt Pleasant, siding installation Scottdale, deck builder Connellsville, flooring Uniontown, home repairs Fayette County, licensed contractor PA 15666"
      />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="https://images.unsplash.com/photo-1562259929-b4e1fd3aef09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxob21lJTIwaW1wcm92ZW1lbnQlMjBjb250cmFjdG9yfGVufDB8fHx8MTc2NTMwNzg2Nnww&ixlib=rb-4.1.0&q=85"
            alt="Professional home improvement"
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>

        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title" style={{color: '#ffffff'}}>
              SBF HOME IMPROVEMENTS
            </h1>
            <h2 className="body-large" style={{color: '#ffffff', marginTop: '20px', fontWeight: '600', fontSize: '1.75rem'}}>
              Expert Roofing, Siding, Flooring & Repairs – Built to Last
            </h2>
            <p className="body-medium" style={{color: '#ffffff', marginTop: '16px', maxWidth: '40ch', lineHeight: '1.6'}}>
              Professional craftsmanship you can trust. Fully licensed & insured in Pennsylvania.
            </p>
            <p className="body-medium" style={{color: '#ffffff', marginTop: '12px', maxWidth: '40ch', lineHeight: '1.6'}}>
              Protect your home with quality work that stands the test of time.
            </p>
            <p className="body-large" style={{color: '#ffffff', marginTop: '20px', fontWeight: '700', fontSize: '1.5rem'}}>
              Get Your Free Estimate Today
            </p>
            
            <div style={{display: 'flex', gap: '16px', marginTop: '32px', flexWrap: 'wrap'}}>
              <Link to="/contact" className="btn-primary" style={{fontSize: '1.25rem', padding: '1.25em 2em', fontWeight: '700'}}>
                GET FREE QUOTE
              </Link>
              <a href={`tel:${businessInfo.phone}`} className="btn-secondary">
                <Phone size={20} style={{marginRight: '8px'}} />
                CALL NOW
              </a>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginTop: '32px'}}>
              <CheckCircle2 size={20} color="#ffffff" />
              <span className="caption" style={{color: '#ffffff'}}>PA LICENSE #{businessInfo.license}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section style={{background: 'var(--bg-page)', padding: '80px 40px'}}>
        <div className="container">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '40px', textAlign: 'center'}}>
            <div>
              <div className="heading-2" style={{color: 'var(--brand-primary)', marginBottom: '8px'}}>15+</div>
              <p className="body-small">Years Experience</p>
            </div>
            <div>
              <div className="heading-2" style={{color: 'var(--brand-primary)', marginBottom: '8px'}}>500+</div>
              <p className="body-small">Projects Completed</p>
            </div>
            <div>
              <div className="heading-2" style={{color: 'var(--brand-primary)', marginBottom: '8px'}}>100%</div>
              <p className="body-small">Licensed & Insured</p>
            </div>
            <div>
              <div className="heading-2" style={{color: 'var(--brand-primary)', marginBottom: '8px'}}>5★</div>
              <p className="body-small">Customer Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section style={{background: 'var(--bg-card)', padding: '96px 40px'}}>
        <div className="container">
          <div style={{marginBottom: '60px'}}>
            <h2 className="heading-1" style={{color: 'var(--text-primary)', marginBottom: '16px'}}>OUR SERVICES</h2>
            <p className="body-medium" style={{color: 'var(--text-secondary)', maxWidth: '50ch'}}>From roof replacements to flooring installation, we deliver quality workmanship on every project.</p>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px'}}>
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section style={{background: 'var(--bg-page)', padding: '96px 40px'}}>
        <div className="container">
          <div style={{marginBottom: '60px', textAlign: 'center'}}>
            <h2 className="heading-1" style={{color: 'var(--text-primary)', marginBottom: '16px'}}>BEFORE & AFTER</h2>
            <p className="body-medium" style={{color: 'var(--text-secondary)', margin: '0 auto', maxWidth: '50ch'}}>See the transformation. Real projects, real results.</p>
          </div>
          <GalleryGrid items={gallery.slice(0, 4)} />
          <div style={{textAlign: 'center', marginTop: '48px'}}>
            <Link to="/gallery" className="btn-primary">
              VIEW FULL GALLERY
              <ArrowRight size={20} style={{marginLeft: '8px'}} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{background: 'var(--bg-card)', padding: '96px 40px'}}>
        <div className="container">
          <div style={{marginBottom: '60px', textAlign: 'center'}}>
            <h2 className="heading-1" style={{color: 'var(--text-primary)', marginBottom: '16px'}}>WHAT OUR CLIENTS SAY</h2>
            <div style={{display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '16px'}}>
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={24} fill="var(--brand-primary)" color="var(--brand-primary)" />
              ))}
            </div>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px'}}>
            {testimonials.slice(0, 3).map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{background: 'var(--brand-primary)', padding: '80px 40px'}}>
        <div className="container" style={{textAlign: 'center'}}>
          <h2 className="heading-1" style={{color: 'var(--text-inverse)', marginBottom: '16px'}}>READY TO START YOUR PROJECT?</h2>
          <p className="body-large" style={{color: 'var(--text-inverse)', marginBottom: '40px', maxWidth: '50ch', margin: '0 auto 40px'}}>Get your free estimate today. No obligation, just honest advice and quality service.</p>
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <Link to="/contact" className="btn-secondary" style={{background: 'var(--text-inverse)', color: 'var(--brand-primary)', border: 'none'}}>
              REQUEST QUOTE
            </Link>
            <a href={`tel:${businessInfo.phone}`} className="btn-secondary" style={{background: 'transparent', color: 'var(--text-inverse)', borderColor: 'var(--text-inverse)'}}>
              <Phone size={20} style={{marginRight: '8px'}} />
              {businessInfo.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Hearth Financing Banner - LARGE */}
      <section style={{background: 'var(--brand-primary)', padding: '80px 40px'}}>
        <div className="container" style={{textAlign: 'center'}}>
          <h2 className="heading-1" style={{color: 'white', marginBottom: '24px'}}>FLEXIBLE FINANCING OPTIONS</h2>
          <p className="body-large" style={{color: 'white', marginBottom: '40px', maxWidth: '60ch', margin: '0 auto 40px'}}>
            Make your home improvement dreams a reality with easy monthly payments
          </p>
          <a 
            href="https://app.gethearth.com/partners/sbf-home-improvements-llc?utm_campaign=30544&utm_content=grey&utm_medium=contractor-website&utm_source=contractor&utm_term=700x110" 
            target="_blank"
            rel="noopener noreferrer"
            style={{display: 'inline-block'}}
            className="hearth-banner-link"
          >
            <img 
              src="https://app.gethearth.com/contractor_images/sbf-home-improvements-llc/banner.jpg?color=grey&size_id=700x110"
              alt="Hearth Financing - Flexible payment options for your home improvement project"
              style={{width: '100%', maxWidth: '900px', height: 'auto', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'}}
            />
          </a>
          <p className="body-medium" style={{color: 'white', marginTop: '32px', fontWeight: '600'}}>
            ✓ Fast Approval  ✓ Competitive Rates  ✓ Flexible Terms
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;